import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { Tab, Tabs } from '@mui/material'
import { getValidSearchQuery, SearchFocusType } from '@utils/searchUtils'
import { getSearchResultsLink } from './SearchResultsNavigationLink'
import { useRouter } from 'next/router'

const useStyles = makeStyles()((theme) => ({
  tabsRoot: {
    minHeight: 30,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
  indicator: {
    backgroundColor: theme.palette.text.primary,
  },
}))

interface MobileSearchWithinProps {
  orgId?: string
}

const MobileSearchWithin = ({ orgId }: MobileSearchWithinProps) => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  const router = useRouter()
  const validSearchQuery = getValidSearchQuery({
    focus: router.query.focus,
    term: router.query.term,
  })

  const formattedOrgId = useMemo(
    () => (orgId?.trim() === 'UCB' ? 'UCB' : orgId?.trim()?.toLowerCase()),
    [orgId]
  )

  const showSearchNavItem = (focus: string): boolean =>
    validSearchQuery.term && !orgId ? true : validSearchQuery.focus === focus

  const renderProductsTab = () => {
    if (
      !showSearchNavItem(SearchFocusType.Products) &&
      !showSearchNavItem(SearchFocusType.B2B)
    ) {
      return null
    }

    if (!orgId) {
      return (
        <Tab
          value={SearchFocusType.Products}
          label={formatMessage({ id: 'PRODUCTS', defaultMessage: 'Products' })}
          component={getSearchResultsLink(
            SearchFocusType.Products,
            'srp-nav-products'
          )}
        />
      )
    }

    return (
      <Tab
        component="span"
        label={`${formattedOrgId} ${formatMessage({ id: 'PRODUCTS', defaultMessage: 'Products' })}`}
      />
    )
  }

  return (
    <nav aria-label="Main Navigation">
      <Tabs
        variant="scrollable"
        scrollButtons={false}
        value={validSearchQuery.focus}
        classes={{
          root: classes.tabsRoot,
          indicator: classes.indicator,
        }}
      >
        {renderProductsTab()}
        {showSearchNavItem(SearchFocusType.TechnicalDocuments) && (
          <Tab
            value={SearchFocusType.TechnicalDocuments}
            label={formatMessage({
              id: 'TECHNICAL_DOCUMENTS',
              defaultMessage: 'Technical Documents',
            })}
            component={getSearchResultsLink(
              SearchFocusType.TechnicalDocuments,
              'srp-nav-techdocs'
            )}
          />
        )}
        {showSearchNavItem(SearchFocusType.SiteContent) && (
          <Tab
            value={SearchFocusType.SiteContent}
            label={formatMessage({
              id: 'SITE_CONTENT',
              defaultMessage: 'Site Content',
            })}
            component={getSearchResultsLink(
              SearchFocusType.SiteContent,
              'srp-nav-sitecontent'
            )}
          />
        )}
      </Tabs>
    </nav>
  )
}

export default MobileSearchWithin
